import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FacturacionService } from '../../../core/services/facturacion.service';
import { MetaService } from '../../../core/services/meta.service';
import { WebAppConfigService } from '../../../core/services/webapp-config.service';
import { API_CONFIG } from '../../../core/config/api.config';

interface PayPhoneConfirmRequest {
  id: number;
  clientTxId: string;
}

interface PayPhoneConfirmResponse {
  email: string;
  cardType: string;
  bin: string;
  lastDigits: string;
  deferredCode: string;
  deferredMessage: string;
  deferred: boolean;
  cardBrandCode: string | null;
  cardBrand: string;
  amount: number;
  clientTransactionId: string;
  phoneNumber: string;
  statusCode: number;
  transactionStatus: string;
  authorizationCode: string;
  message: string;
  messageCode: number;
  transactionId: number;
  document: string;
  taxes: any[];
  currency: string;
  optionalParameter1: string | null;
  optionalParameter2: string | null;
  optionalParameter3: string | null;
  optionalParameter4: string | null;
  storeName: string;
  date: string;
  regionIso: string;
  transactionType: string;
  recap: string | null;
  reference: string;
  pan: string | null;
}

interface InvoiceResponse {
  success: boolean;
  message: string;
  data: {
    factura: any;
    tickets: any[];
    newUserCreated: boolean;
    login?: {
      user: {
        profile: any;
        role: any;
        facturacion: any;
      };
      accessToken: string;
      refreshToken?: string;
      expiresIn: string;
      message: string;
    };
  };
  timestamp: string;
}

interface PayphoneCartSnapshot {
  cartid: string;
  userid: string | null;
  eventoid: string;
  estado: string;
  evento?: {
    url_slug?: string;
  };
  contacto: any;
  informacionfactura: any;
  pasarela_pago: {
    eventometodopagoid: string;
    nombre: string;
    comision: number;
    pasarelaData: any;
  };
  ticketformsdata?: any[];
}

interface PasarelaPago {
  eventometodopagoid: string;
  nombre: string;
  comision: number;
  pasarelaData: any;
}

@Component({
  selector: 'app-payphone-confirm',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payphone-confirm.html',
  styleUrls: ['./payphone-confirm.css']
})
export class PayphoneConfirmComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private facturacionService = inject(FacturacionService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private metaService = inject(MetaService);
  private webAppConfig = inject(WebAppConfigService);
  private isBrowser: boolean;
  private currentCart: PayphoneCartSnapshot | null = null;
  private hasStartedConfirmationFlow: boolean = false;
  private isCreatingInvoice: boolean = false;
  private invoiceRetryAttempts: number = 0;
  private readonly maxInvoiceRetryAttempts: number = 4;

  transactionId: string = '';
  clientTransactionId: string = ''; // cartid (nuevo flujo)
  confirmResponse: PayPhoneConfirmResponse | null = null;
  invoiceResponse: InvoiceResponse | null = null;
  isLoading: boolean = false;
  error: string | null = null;
  paymentData: any = null;
  eventSlug: string | null = null;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }

    if (!this.isBrowser) {
      console.log('⚠️ Componente ejecutándose en el servidor (SSR), esperando navegador...');
      return;
    }

    this.clearStaleFacturacionData();

    // Obtener parámetros de la URL
    this.route.queryParams.subscribe(params => {
      console.log('🔔 Confirmación de pago Payphone - Query Params:', params);
      this.paymentData = params;
      
      this.transactionId = params['id'] || '';
      this.clientTransactionId = params['clientTransactionId'] || '';

      console.log('📥 Parámetros recibidos de PayPhone:');
      console.log('  - Transaction ID:', this.transactionId);
      console.log('  - Client Transaction ID (paymentid):', this.clientTransactionId);

      if (this.transactionId && this.clientTransactionId) {
        if (this.hasStartedConfirmationFlow) {
          console.warn('⚠️ Flujo de confirmación ya iniciado, se ignora invocación duplicada');
          return;
        }
        this.hasStartedConfirmationFlow = true;
        this.validateCartAndConfirm();
      } else {
        this.error = 'Faltan parámetros en la URL';
        console.error('❌ Faltan parámetros:', params);
      }
    });

    // Obtener datos del state de la navegación
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      console.log('🔔 Confirmación de pago Payphone - State:', navigation.extras.state);
      this.paymentData = { ...this.paymentData, ...navigation.extras.state };
    }

    // También escuchar mensajes postMessage (si Payphone usa iframe/popup)
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handlePostMessage.bind(this));
    }
  }

  private clearStaleFacturacionData(): void {
    const existing = this.facturacionService.getFacturacionData();
    if (existing) {
      this.facturacionService.clearFacturacionData();
      console.log('🧹 Se limpiaron datos residuales de FacturacionService en confirmación Payphone');
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser && typeof window !== 'undefined') {
      window.removeEventListener('message', this.handlePostMessage.bind(this));
    }
  }

  private handlePostMessage(event: MessageEvent): void {
    console.log('📬 Mensaje recibido de Payphone:', {
      origin: event.origin,
      data: event.data
    });
    
    // Validar origen si es necesario
    // if (event.origin !== 'https://payphone-expected-origin.com') return;
    
    this.paymentData = { ...this.paymentData, postMessage: event.data };
  }

  private validateCartAndConfirm(): void {
    this.isLoading = true;
    this.error = null;

    const cartid = this.clientTransactionId;
    const endpoint = API_CONFIG.endpoints.carrito.getById.replace('{cartid}', cartid);

    this.http.get<any>(`${API_CONFIG.baseUrl}${endpoint}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    }).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const cart = response?.data;
          if (!response?.success || !cart) {
            this.error = 'No se encontró el carrito para confirmar pago';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          // Si ya se completó antes (por ejemplo webhook/proceso previo), no confirmar de nuevo.
          if (cart.estado === 'completado') {
            if (cart.pasarela_pago?.pasarelaData) {
              this.confirmResponse = cart.pasarela_pago.pasarelaData;
            }
            this.error = 'Este pago ya fue procesado previamente.';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          // El carrito expiró mientras el usuario estaba en la pasarela.
          if (cart.estado === 'expirado') {
            this.eventSlug = cart.evento?.url_slug || null;
            this.error = 'Tu reserva expiró mientras se procesaba el pago. Por favor regresa al evento y vuelve a seleccionar tus entradas.';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          if (cart.estado !== 'pagando' && cart.estado !== 'activo') {
            this.error = `El carrito no está en estado válido para confirmar pago: ${cart.estado}`;
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.currentCart = cart;

          this.confirmPayment();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Error al validar carrito para confirmación:', error);
          this.error = 'No se pudo validar el estado del carrito';
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  confirmPayment() {
    this.isLoading = true;
    this.error = null;

    if (!this.currentCart) {
      console.error('❌ No se encontró el carrito para confirmar pago');
      this.error = 'Error: Datos del carrito no disponibles';
      this.isLoading = false;
      return;
    }

    const { eventoid } = this.currentCart;
    const eventometodopagoid = this.currentCart.pasarela_pago?.eventometodopagoid;

    if (!eventometodopagoid) {
      console.error('❌ No se encontró eventometodopagoid en el carrito');
      this.error = 'Error: Método de pago no disponible en el carrito';
      this.isLoading = false;
      return;
    }

    console.log('📡 Obteniendo token del método de pago...');
    console.log('  - EventoID:', eventoid);
    console.log('  - MetodoPagoID:', eventometodopagoid);

    // Obtener el método de pago para conseguir el token
    const endpoint = API_CONFIG.endpoints.eventos.getMetodoPago
      .replace('{eventoid}', eventoid)
      .replace('{metodopagoid}', eventometodopagoid);
    
    this.http.get<any>(
      `${API_CONFIG.baseUrl}${endpoint}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        }
      }
    ).subscribe({
      next: (metodoPagoResponse) => {
        console.log('✅ Método de pago obtenido:', metodoPagoResponse);

        const payphoneToken = metodoPagoResponse.data?.tokenId;

        if (!payphoneToken) {
          console.error('❌ No se encontró el token en el método de pago');
          this.error = 'Error: Token de autenticación no disponible';
          this.isLoading = false;
          return;
        }

        const requestBody: PayPhoneConfirmRequest = {
          id: parseInt(this.transactionId),
          clientTxId: this.clientTransactionId
        };

        console.log('📡 Confirmando pago con PayPhone:', requestBody);
        console.log('🔑 Usando token:', payphoneToken.substring(0, 30) + '...');

        this.http.post<PayPhoneConfirmResponse>(
          'https://pay.payphonetodoesposible.com/api/button/v2/Confirm',
          requestBody,
          {
            headers: {
              'Authorization': `Bearer ${payphoneToken}`
            }
          }
        ).subscribe({
          next: (response) => {
            console.log('✅ Respuesta de confirmación de PayPhone:', response);
            console.log('📊 Estado de la transacción:', response.transactionStatus);
            console.log('💰 Monto:', response.amount);
            
            // Ejecutar dentro de la zona de Angular para asegurar detección de cambios
            this.ngZone.run(() => {
              this.confirmResponse = response;
              
              console.log('🔄 Estado actualizado:');
              console.log('  - isLoading:', this.isLoading);
              console.log('  - confirmResponse:', this.confirmResponse ? 'Presente' : 'null');
              console.log('  - transactionStatus:', this.confirmResponse?.transactionStatus);
              
              // Forzar detección de cambios explícitamente
              this.cdr.markForCheck();
              this.cdr.detectChanges();
              console.log('🔄 detectChanges() ejecutado');
              
              // Si el pago fue aprobado, crear la factura
              if (response.transactionStatus === 'Approved') {
                console.log('✅ Pago aprobado, creando factura...');
                this.createInvoice();
              } else {
                this.isLoading = false;
                console.log('⚠️ Pago no aprobado, estado:', response.transactionStatus);
                // Registrar fallo de pago
                this.registerPaymentFailure(response);
              }
            });
          },
          error: (error) => {
            console.error('❌ Error al confirmar pago:', error);
            console.error('📋 Detalles del error:', JSON.stringify(error, null, 2));
            
            this.ngZone.run(() => {
              this.error = 'Error al confirmar el pago. Por favor, contacte con soporte.';
              this.isLoading = false;
              // Registrar fallo de pago
              this.registerPaymentFailure({
                error_code: error.status,
                error_message: error.message || 'Error al confirmar pago',
                error_details: error
              });
            });
          }
        });
      },
      error: (error) => {
        console.error('❌ Error al obtener el método de pago:', error);
        console.error('📋 Detalles del error:', JSON.stringify(error, null, 2));
        
        this.ngZone.run(() => {
          this.error = 'Error al obtener el método de pago. Por favor, contacte con soporte.';
          this.isLoading = false;
          // Registrar fallo de pago
          this.registerPaymentFailure({
            error_code: error.status,
            error_message: 'Error al obtener método de pago',
            error_details: error
          });
        });
      }
    });
  }

  /**
   * Crear factura después de confirmar el pago exitoso.
   * En el nuevo flujo clientTransactionId coincide con cartid.
   */
  createInvoice() {
    console.log('📝 Iniciando creación de factura...');

    if (this.isCreatingInvoice || this.invoiceResponse) {
      console.warn('⚠️ Se bloqueó un intento duplicado de creación de factura');
      return;
    }
    this.isCreatingInvoice = true;

    if (!this.currentCart) {
      console.error('❌ No se encontraron datos del carrito');
      this.error = 'Error: No se encontró la información del carrito';
      this.isLoading = false;
      this.isCreatingInvoice = false;
      return;
    }

    console.log('📋 Datos de carrito para factura:', this.currentCart);

    // Preparar el body de la petición según la estructura requerida
    const invoiceRequest = {
      cartid: this.currentCart.cartid,
      userid: this.currentCart.userid || this.currentCart.contacto?.userid || null,
      eventoid: this.currentCart.eventoid,
      contacto: this.currentCart.contacto,
      informacionfactura: this.currentCart.informacionfactura,
      eventometodopagoid: this.currentCart.pasarela_pago?.eventometodopagoid,
      ticketformsdata: this.currentCart.ticketformsdata || [],
      pasarelaData: this.confirmResponse
    };

    console.log('📡 Enviando petición de factura:', invoiceRequest);

    this.http.post<InvoiceResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.facturas.create}`,
      invoiceRequest,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        },
        withCredentials: API_CONFIG.cookie_http_only
      }
    ).subscribe({
      next: (response) => {
        console.log('✅ Factura creada exitosamente:', response);
        
        this.ngZone.run(() => {
          // Guardar la respuesta de la factura
          this.invoiceResponse = response;
          this.isLoading = false;
          
          // Forzar detección de cambios
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          
          // Si se creó un nuevo usuario y viene el login en la respuesta
          if (response.data?.newUserCreated && response.data?.login) {
            console.log('🆕 Nuevo usuario creado, guardando token...');
            const loginData = response.data.login;
            
            if (!API_CONFIG.cookie_http_only) {
              // Guardar el token en localStorage
              if (loginData.accessToken) {
                localStorage.setItem('access_token', loginData.accessToken);
                console.log('🔑 Token de acceso guardado');
              }
              
              // Si viene refresh token, también guardarlo
              if (loginData.refreshToken) {
                localStorage.setItem('refresh_token', loginData.refreshToken);
                console.log('🔄 Token de refresh guardado');
              }

              // Guardar información del usuario
              if (loginData.user) {
                localStorage.setItem('user_info', JSON.stringify(loginData.user));
                console.log('👤 Información de usuario guardada');
              }
            }
          }

          console.log('✅ Proceso de pago completado exitosamente');
          this.invoiceRetryAttempts = 0;
          this.isCreatingInvoice = false;
        });
      },
      error: (error) => {
        console.error('❌ Error al crear la factura:', error);
        console.error('📋 Detalles del error:', JSON.stringify(error, null, 2));

        const backendMessage = String(error?.error?.message || '').toLowerCase();
        const isInvoiceInProgress = backendMessage.includes('invoice creation already in progress for this cart');

        if (isInvoiceInProgress && this.invoiceRetryAttempts < this.maxInvoiceRetryAttempts) {
          this.invoiceRetryAttempts++;
          console.warn(`⏳ Factura en procesamiento, reintentando (${this.invoiceRetryAttempts}/${this.maxInvoiceRetryAttempts})...`);
          this.isCreatingInvoice = false;

          setTimeout(() => {
            this.createInvoice();
          }, 1200);
          return;
        }
        
        this.ngZone.run(() => {
          this.error = 'Error al crear la factura. Por favor, contacte con soporte.';
          this.isLoading = false;
          // Registrar fallo de pago
          this.registerPaymentFailure({
            error_code: error.status,
            error_message: 'Error al crear factura',
            error_details: error,
            payphone_response: this.confirmResponse
          });
          this.invoiceRetryAttempts = 0;
          this.isCreatingInvoice = false;
        });
      }
    });
  }

  getStatusColor(): string {
    if (!this.confirmResponse) return 'gray';
    
    switch (this.confirmResponse.transactionStatus) {
      case 'Approved':
        return 'green';
      case 'Canceled':
        return 'red';
      case 'Pending':
        return 'orange';
      default:
        return 'gray';
    }
  }

  getStatusIcon(): string {
    if (!this.confirmResponse) return '❓';
    
    switch (this.confirmResponse.transactionStatus) {
      case 'Approved':
        return '✅';
      case 'Canceled':
        return '❌';
      case 'Pending':
        return '⏳';
      default:
        return '❓';
    }
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  goBackToEvent(): void {
    if (this.eventSlug) {
      this.router.navigate(['/evento', this.eventSlug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  returnToHome() {
    this.router.navigate(['/']);
  }

  /**
   * Registrar fallo de pago
   */
  registerPaymentFailure(errorData?: any): void {
    if (!this.currentCart) {
      console.error('❌ No se encontraron datos de carrito para registrar el fallo');
      return;
    }

    // Construir objeto PasarelaPago según la interfaz
    const pasarelaPago: PasarelaPago = {
      eventometodopagoid: this.currentCart.pasarela_pago?.eventometodopagoid,
      nombre: 'PAYPHONE',
      comision: 0,
      pasarelaData: errorData || this.confirmResponse || {
        error: this.error,
        paymentData: this.paymentData
      }
    };

    const requestBody = {
      cartid: this.currentCart.cartid,
      pasarela_pago: pasarelaPago
    };

    console.log('🚨 Registrando fallo de pago:', requestBody);

    this.http.post(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.paymentFailure}`,
      requestBody,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    ).subscribe({
      next: (response) => {
        console.log('✅ Fallo de pago registrado:', response);
      },
      error: (error) => {
        console.error('❌ Error al registrar fallo de pago:', error);
      }
    });
  }
}
